import {ref, onMounted} from 'vue'
import {defineStore} from 'pinia'
import i18next from 'i18next'
import {useLocalStore} from './local'
import router from '@/router'
import {api, authApi, profileApi} from '@/api'
import type {Profile, ProfileSettings, SavedProfile, UserSettings} from '@shared/types'
import {useControlPanelUsersStore} from './controlPanelUsers'

export const useUserStore = defineStore('user', () => {
  const isLoading = ref(false)
  const user = ref<{email: string; role: string; settings: UserSettings | null} | null>(null)
  const profile = ref<SavedProfile | null>(null)
  const isSigningIn = ref(false)
  let userRequestGeneration = 0

  async function fetchUser() {
    const requestGeneration = ++userRequestGeneration
    const response = await api.get('/api/user')
    if (requestGeneration === userRequestGeneration && !isSigningIn.value) {
      user.value = await response.data
      const interfaceLanguage = user.value?.settings?.interface_language
      if (interfaceLanguage) i18next.changeLanguage(interfaceLanguage)
    }
  }

  async function updateUserSettings(patch: Partial<UserSettings>): Promise<void> {
    if (!user.value) return
    const settings = {...user.value.settings, ...patch} as UserSettings
    await api.put('/api/user/settings', patch)
    user.value = {...user.value, settings}
    if (patch.interface_language) i18next.changeLanguage(patch.interface_language)
  }

  async function signInWithPassword(email: string, password: string) {
    userRequestGeneration++
    isSigningIn.value = true
    try {
      user.value = await authApi.signInWithPassword(email, password)
      await router.push('/user')
    } finally {
      isSigningIn.value = false
    }
  }

  async function fetchProfile() {
    const profileId = useLocalStore().profileId
    if (!user.value || !profileId) {
      return
    }
    try {
      const response = await api.get(`/api/profile/${profileId}`)
      profile.value = (await response.data) as SavedProfile
    } catch (error) {
      const status = (error as {response?: {status?: number}})?.response?.status
      if (status !== 404) throw error
      useLocalStore().profileId = NaN
      profile.value = null
    }
  }

  async function selectProfile(profileId: number): Promise<void> {
    const localStore = useLocalStore()
    localStore.profileId = profileId
    profile.value = null
    await fetchProfile()
  }

  async function ensureProfileSelected(): Promise<void> {
    if (!user.value) await fetchUser()
    if (profile.value || !user.value) return

    const profiles = await profileApi.list()
    if (!profiles.length) return

    const localStore = useLocalStore()
    const selectedProfile = profiles.find((item) => item.profile_id === localStore.profileId) ?? profiles[0]
    await selectProfile(selectedProfile.profile_id)
  }

  async function updateProfileSettings(patch: Partial<ProfileSettings>): Promise<void> {
    if (!profile.value) return
    const settings = {...profile.value.settings, ...patch}
    await profileApi.update(profile.value.profile_id, {settings})
    profile.value = {...profile.value, settings}
  }

  async function signOut() {
    userRequestGeneration++
    const localStore = useLocalStore()
    const controlPanelUsersStore = useControlPanelUsersStore()
    try {
      await api.get('/api/auth/logout')
    } catch (error) {
      console.error('Error signing out:', error)
    } finally {
      user.value = null
      profile.value = null
      localStore.profileId = NaN
      controlPanelUsersStore.reset()
      await router.push('/auth')
    }
  }

  onMounted(async () => {
    try {
      isLoading.value = true
      await fetchUser()
      if (user.value && router.currentRoute.value.path === '/auth') {
        await router.replace('/')
      }
      await fetchProfile()
    } finally {
      isLoading.value = false
    }
  })

  return {
    user,
    profile,
    fetchUser,
    signOut,
    isLoading,
    fetchProfile,
    selectProfile,
    ensureProfileSelected,
    updateProfileSettings,
    updateUserSettings,
    signInWithPassword,
  }
})
